import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  SnackbarContent,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCloseNotification, useNotify } from 'react-admin';
import authInstance from '../../services/httpClient';
import { useAdminTheme } from '../../admin-theme';
import { FONT_DATA, FONT_DISPLAY } from '../../theme/grounds';
import Logger from '../../utils/Logger';

/**
 * Tablet health: whether each order tablet is on (green/red dot), whether Order Manager
 * is signed in on it (a red NOT LOGGED INTO ORDER MANAGER line when it is on but the app
 * is not checking in with us), plus what Esper (the tablets' MDM) last heard from it. Owner/admin only;
 * the backend refuses everyone else and can send no command but these two:
 * - Refresh: asks THAT tablet to check in with Esper now (seconds, if it is on).
 * - Reboot.
 * Reload (top right) contacts no tablet; it re-reads what Esper already holds.
 *
 * API: GET /admin/tablet-health, GET /admin/tablet-health/:deviceId/command,
 * POST /admin/tablet-health/:deviceId/{refresh|reboot}.
 * Idle tablets report roughly hourly, so every reading carries its "as of" time.
 */

/** Whether the tablet runs the newest Order Manager it can: APK (Esper) + OTA (expo-updates). */
interface Software {
  state: 'upToDate' | 'needsOta' | 'needsApk' | 'unknown';
  label: string;
  detail: string;
  runningUpdateId: string | null;
  latestUpdateId: string | null;
}

interface Tablet {
  id: string;
  name: string | null;
  alias: string | null;
  state: string;
  lastSeen: string | null;
  lastCheckIn: string | null;
  snapshotAt: string | null;
  model: string | null;
  serial: string | null;
  store: { id: string; name: string | null; externalId: string | null } | null;
  app: { versionName: string | null; versionCode: string | null } | null;
  os: { version: string | null; build: string | null; securityPatch: string | null };
  memory: { totalRamMb: number | null; availableRamMb: number | null; totalStorageMb: number | null; availableStorageMb: number | null };
  battery: { level: number | null; pluggedIn: boolean | null; powerSource: string | null; temperatureC: number | null; health: string | null; holding?: boolean };
  network: { ssid: string | null; signal: number | null; linkSpeedMbps: number | null; band: string | null; ipAddress: string | null };
  bluetooth: { enabled: boolean | null; paired: string[]; connected: string[] };
  settings: { volumes: Record<string, number | null>; brightness: number | null; screenOffTimeoutMs: number | null; rotation: string | null };
  location: { lat: number; lng: number } | null;
  cpuTemperatureC: number | null;
  blueprint?: Blueprint | null;
  activity: { message: string; at: string | null }[];
  online?: boolean;
  offlineReason?: string | null;
  /** Order Manager signed in and checking in with us (false when its store is not on this server). */
  appSignedIn?: boolean | null;
  /** When the tablet app last checked in with us (X-Tablet-Store); null before the app sends it. */
  tabletSeenAt?: string | null;
  screenOffSince?: string | null;
  /** Needs APK / Needs OTA, worked out by the backend (tablet-health/ota-status.js). */
  software?: Software;
  /**
   * Which environment may Refresh / Reboot this tablet. Both dashboards list the whole Esper
   * fleet; only the owning one (judged by live check-in, then the fleet list) gets working buttons.
   */
  environment?: { name: string | null; own: boolean; source: 'check-in' | 'fleet-list' | null; reason: string } | null;
  lastCommand?: LastCommand | null;
}

/** Older backends send no environment: treat the tablet as ours so the buttons keep working. */
const ours = (t: Tablet) => !t.environment || t.environment.own;
const envLabel = (t: Tablet) => (t.environment?.name ? t.environment.name.charAt(0).toUpperCase() + t.environment.name.slice(1) : 'Unassigned');

/** The Esper blueprint the tablet runs. name is null when our Esper key may not read blueprints. */
interface Blueprint {
  id: string | null;
  name: string | null;
  version: number | null;
  latestVersion: number | null;
  /** On an older version than the blueprint's latest published one. */
  behind: boolean;
  /** Assigned another blueprint it has not moved to yet. */
  moving: boolean;
  assignedName: string | null;
}

interface LastCommand {
  action: 'refresh' | 'reboot';
  stage: 'queued' | 'running' | 'done' | 'failed';
  sentAt: string | null;
  updatedAt: string | null;
}

interface HealthResponse {
  fetchedAt: string;
  fleet?: { seenLast30Min: number | null; seenLastDay: number | null; notSeenOverDay: number | null; lowBattery: number | null } | null;
  tablets: Tablet[];
}

type Level = 'ok' | 'crit' | 'none';

const LOW_BATTERY = 20;
/** How long a finished command stays on the row ("Rebooted 2 min ago"). */
const RECENT_COMMAND_MIN = 10;

const minutesSince = (iso: string | null) => (iso ? (Date.now() - new Date(iso).getTime()) / 60000 : null);

const relative = (iso: string | null) => {
  const m = minutesSince(iso);
  if (m === null) return '—';
  if (m < 1) return 'just now';
  if (m < 60) return `${Math.round(m)} min ago`;
  if (m < 48 * 60) return `${Math.round(m / 60)} h ago`;
  return `${Math.round(m / 1440)} d ago`;
};

const clock = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const label = (t: Tablet) => t.alias || t.store?.name || t.name || 'Tablet';

/** A tablet is either on or off: green or red, nothing in between. */
/** These notices are a sentence or two; react-admin's 4 s default is gone before they are read. */
const NOTICE_MS = 6_000;
/** Page reload interval while the tab is visible. */
const POLL_MS = 5 * 60_000;
/** Coming back to the tab reloads if the table is older than this. */
const SHOW_RELOAD_MS = 60_000;
/**
 * Progress checks after a button press (2 Esper calls each). An awake tablet answers a
 * Refresh in ~4 s, so the first check (sent at once) or the next usually catches it; one that hasn't in a minute is asleep and answers whenever it wakes,
 * which the row then shows as "Check-in waiting". A reboot takes ~1 min end to end.
 */
const WATCH = {
  refresh: { every: 10_000, limit: 60_000 },
  reboot: { every: 15_000, limit: 3 * 60_000 },
};

/** A notice that also closes when clicked, not only after NOTICE_MS. */
const Notice = ({ text }: { text: string }) => {
  const close = useCloseNotification();
  return <SnackbarContent message={text} onClick={close} sx={{ cursor: 'pointer' }} />;
};

const isOnline = (t: Tablet) => t.online === true;
const notSignedIn = (t: Tablet) => isOnline(t) && t.appSignedIn === false;
// Store screens stay on, so off = power button pressed. Only meaningful while the app reports in.
const screenOff = (t: Tablet) => t.appSignedIn === true && Boolean(t.screenOffSince);
const newest = (...isos: (string | null | undefined)[]) =>
  isos.filter(Boolean).sort((a, b) => Date.parse(b as string) - Date.parse(a as string))[0] || null;

/** The words for where a Refresh/Reboot has got to, or null when there is nothing to say. */
function commandText(c: LastCommand | null | undefined): { text: string; level: Level } | null {
  if (!c) return null;
  const reboot = c.action === 'reboot';
  switch (c.stage) {
    case 'queued': {
      const waited = minutesSince(c.sentAt);
      const late = waited !== null && waited >= 1;
      return {
        // Usually an unplugged tablet asleep: Android holds Esper's messages until it
        // wakes (Bedford, 11 Oct). Not red: it is waiting, not failing.
        text: reboot
          ? `Reboot waiting${late ? ' — tablet asleep? It goes through once the tablet is plugged in or its screen wakes' : '…'}`
          : `Check-in waiting${late ? ' — tablet asleep? It goes through once the tablet is plugged in or its screen wakes' : '…'}`,
        level: 'none',
      };
    }
    case 'running':
      return { text: reboot ? 'Rebooting…' : 'Checking in…', level: 'none' };
    case 'failed':
      return { text: reboot ? 'Reboot failed' : 'Check-in failed', level: 'crit' };
    case 'done': {
      const ago = minutesSince(c.updatedAt);
      if (ago === null || ago > RECENT_COMMAND_MIN) return null;
      return { text: `${reboot ? 'Rebooted' : 'Checked in'} ${relative(c.updatedAt)}`, level: 'ok' };
    }
    default:
      return null;
  }
}

/** Numeric compare of "1.0.6"-style versions; unknowns sort first. */
const versionKey = (v: string | null | undefined) => (v || '').split('.').map((p) => Number(p) || 0);
const newerVersion = (a: string, b: string) => {
  const x = versionKey(a);
  const y = versionKey(b);
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    if ((x[i] || 0) !== (y[i] || 0)) return (x[i] || 0) > (y[i] || 0) ? a : b;
  }
  return a;
};

/** Version line under the blueprint name: "v7 · v8 out" when the tablet is behind. */
const blueprintVersion = (b: Blueprint) => {
  if (b.version === null) return b.behind ? 'older version' : '';
  return b.behind && b.latestVersion !== null ? `v${b.version} · v${b.latestVersion} out` : `v${b.version}`;
};

const STREAMS: [string, string][] = [['ring', 'Ring'], ['notification', 'Notification'], ['alarm', 'Alarm'], ['music', 'Media']];

const screenTimeout = (ms: number | null) => {
  if (ms === null) return '—';
  if (ms < 0) return 'Never';
  const s = Math.round(ms / 1000);
  return s >= 60 ? `${Math.round(s / 60)} min` : `${s} s`;
};

const TabletHealth: React.FC = () => {
  const { colors, cardStyle } = useAdminTheme();
  const softwareColor = (state: Software['state']) =>
    state === 'needsApk' ? colors.error : state === 'needsOta' ? colors.warning : state === 'upToDate' ? colors.success : colors.textMuted;
  const notify = useNotify();
  const [data, setData] = useState<HealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Set when a reload fails but an earlier one worked: the table stays, with a note.
  const [staleSince, setStaleSince] = useState<Date | null>(null);
  const lastLoaded = useRef<Date | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, 'refresh' | 'reboot' | undefined>>({});
  const [confirm, setConfirm] = useState<Tablet | null>(null);
  // Orders waiting at the store of the tablet in the Reboot dialog; undefined while loading.
  const [waiting, setWaiting] = useState<number | null | undefined>(undefined);
  // Live command progress per tablet while the page polls it after a button press.
  const [live, setLive] = useState<Record<string, LastCommand | null>>({});
  const timers = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  const tone = (l: Level) => (l === 'crit' ? colors.error : l === 'ok' ? colors.success : colors.textMuted);

  const load = useCallback(async (fresh = false, retried = false) => {
    try {
      const { data: body } = await authInstance.get(`/admin/tablet-health${fresh ? '?fresh=true' : ''}`);
      setData(body);
      setError(null);
      setStaleSince(null);
      lastLoaded.current = new Date();
    } catch (err: any) {
      Logger.error('[TabletHealth] load', err);
      // No response at all (laptop waking, Wi-Fi blip): try once more before saying anything.
      if (!err?.response && !retried) {
        setTimeout(() => load(fresh, true), 3_000);
        return;
      }
      // One failed poll must not wipe a table we already have: keep it and flag its age.
      if (lastLoaded.current) {
        setStaleSince(lastLoaded.current);
        return;
      }
      const status = err?.response?.status;
      setError(
        status === 404
          ? 'The tablet-health API is not deployed on this environment yet.'
          : status === 503
            ? err?.response?.data?.userMessage || 'Esper is not configured on this server.'
            : err?.response?.data?.userMessage || err?.message || 'Failed to load tablet health',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Esper is cached on the server for 5 min and urgent problems go to Discord, so a slow
    // poll is enough. A hidden tab does not poll at all; it catches up when shown again.
    const visible = () => document.visibilityState === 'visible';
    const t = setInterval(() => visible() && load(), POLL_MS);
    const onShow = () => {
      const age = lastLoaded.current ? Date.now() - lastLoaded.current.getTime() : Infinity;
      if (visible() && age > SHOW_RELOAD_MS) load();
    };
    document.addEventListener('visibilitychange', onShow);
    const pending = timers.current;
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onShow);
      Object.values(pending).forEach(clearInterval);
    };
  }, [load]);

  /** Polls one tablet's command until it finishes (or we give up), then reloads fresh. */
  const watchCommand = useCallback((t: Tablet, action: 'refresh' | 'reboot') => {
    clearInterval(timers.current[t.id]);
    const started = Date.now();
    const { every, limit } = WATCH[action];
    const tick = async () => {
      // Each check is 2 Esper calls; nobody is watching a hidden tab.
      if (document.visibilityState !== 'visible') return;
      try {
        const { data: body } = await authInstance.get(`/admin/tablet-health/${t.id}/command`);
        const c: LastCommand | null = body?.lastCommand || null;
        setLive((l) => ({ ...l, [t.id]: c }));
        const finished = c && c.action === action && (c.stage === 'done' || c.stage === 'failed');
        if (finished || Date.now() - started > limit) {
          clearInterval(timers.current[t.id]);
          delete timers.current[t.id];
          // Hand the row back to the page data (reloaded fresh just below), or a command that was
          // still queued when we gave up would read "queued" until the page is reloaded.
          setLive((l) => {
            const { [t.id]: _done, ...rest } = l;
            return rest;
          });
          load(true);
        }
      } catch (err) {
        Logger.error('[TabletHealth] command status', err);
      }
    };
    timers.current[t.id] = setInterval(tick, every);
    tick();
  }, [load]);

  const newestApp = useMemo(() => {
    const versions = (data?.tablets || []).map((t) => t.app?.versionName).filter(Boolean) as string[];
    return versions.length ? versions.reduce(newerVersion) : null;
  }, [data]);

  const summary = useMemo(() => {
    const tablets = data?.tablets || [];
    return {
      total: tablets.length,
      online: tablets.filter(isOnline).length,
      notSignedIn: tablets.filter(notSignedIn).length,
      lowBattery: tablets.filter((t) => t.battery.level !== null && t.battery.level < LOW_BATTERY).length,
      unplugged: tablets.filter((t) => t.battery.pluggedIn === false && !t.battery.holding).length,
    };
  }, [data]);

  const run = async (t: Tablet, action: 'refresh' | 'reboot') => {
    setBusy((b) => ({ ...b, [t.id]: action }));
    try {
      await authInstance.post(`/admin/tablet-health/${t.id}/${action}`);
      setLive((l) => ({ ...l, [t.id]: { action, stage: 'queued', sentAt: new Date().toISOString(), updatedAt: null } }));
      notify(
        <Notice
          text={
            !isOnline(t)
              ? `${label(t)} is offline, so the ${action === 'reboot' ? 'reboot' : 'check-in'} is queued. Esper delivers it when the tablet is back on.`
              : action === 'reboot'
                ? `Reboot sent to ${label(t)}. Its row shows the progress; it is usually back in about a minute.`
                : `Asked ${label(t)} to check in. If it is asleep (unplugged, screen off), it answers once it is plugged in or its screen wakes.`
          }
        />,
        { type: isOnline(t) ? 'success' : 'info', autoHideDuration: NOTICE_MS },
      );
      watchCommand(t, action);
    } catch (err: any) {
      Logger.error(`[TabletHealth] ${action}`, err);
      notify(<Notice text={err?.response?.data?.userMessage || `Failed to ${action} ${label(t)}`} />, { type: 'error', autoHideDuration: NOTICE_MS });
    } finally {
      setBusy((b) => ({ ...b, [t.id]: undefined }));
    }
  };

  /** Opens the Reboot dialog and reads, live, how many orders are waiting at that store. */
  const askReboot = async (t: Tablet) => {
    setConfirm(t);
    if (!t.store?.externalId) {
      setWaiting(null);
      return;
    }
    setWaiting(undefined);
    try {
      const { data: body } = await authInstance.get(`/admin/tablet-health/stores/${encodeURIComponent(t.store.externalId)}/pending-orders`);
      setWaiting(body?.pendingOrders ?? null);
    } catch (err) {
      Logger.error('[TabletHealth] pending orders', err);
      setWaiting(null);
    }
  };

  const Dot: React.FC<{ level: Level }> = ({ level }) => (
    <Box component="i" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tone(level), display: 'inline-block', flexShrink: 0 }} />
  );

  const Stat: React.FC<{ value: number; caption: string; level?: Level }> = ({ value, caption, level = 'none' }) => (
    <Box sx={{ ...cardStyle, px: 2, py: 1.25, minWidth: 120 }}>
      <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '1.35rem', lineHeight: 1.1, color: level === 'none' ? colors.textPrimary : tone(level) }}>
        {value}
      </Typography>
      <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.66rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.textMuted }}>
        {caption}
      </Typography>
    </Box>
  );

  const Detail: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Box>
      <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.64rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.textMuted, mb: 0.5 }}>
        {title}
      </Typography>
      <Box sx={{ fontSize: '0.76rem', lineHeight: 1.6, color: colors.textSecondary }}>{children}</Box>
    </Box>
  );

  const cell = { fontSize: '0.78rem', color: colors.textSecondary, whiteSpace: 'nowrap' as const };
  const head = { fontFamily: FONT_DATA, fontSize: '0.64rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' as const, color: colors.textMuted };
  const COLS = 'minmax(150px, 1.4fr) minmax(120px, 1fr) minmax(130px, 1fr) minmax(180px, 1.2fr) 70px 70px 90px 150px';

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>;
  }
  if (error) {
    return <Alert severity="warning">{error}</Alert>;
  }
  if (!data) return null;

  return (
    <Box>
      <Stack direction="row" spacing={1.5} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1.5 }}>
        <Stat value={summary.online} caption={`of ${summary.total} online`} level="ok" />
        {summary.notSignedIn > 0 && <Stat value={summary.notSignedIn} caption="not logged into Order Manager" level="crit" />}
        <Stat value={summary.lowBattery} caption={`below ${LOW_BATTERY}% battery`} level={summary.lowBattery ? 'crit' : 'ok'} />
        <Stat value={summary.unplugged} caption="unplugged" level={summary.unplugged ? 'crit' : 'ok'} />
        <Box sx={{ flex: 1 }} />
        <Stack alignItems="flex-end" justifyContent="center">
          <Typography sx={{ fontSize: '0.66rem', color: colors.textMuted }}>Fetched from Esper {relative(data.fetchedAt)}</Typography>
          {staleSince && (
            <Typography sx={{ fontSize: '0.66rem', color: colors.error }}>
              Couldn&apos;t refresh — showing data from {staleSince.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          )}
          <Tooltip title="Re-reads what Esper already holds for every tablet. It does not contact the tablets: use Refresh on a row to make one check in now.">
            <Button size="small" onClick={() => { setLoading(true); load(true); }} sx={{ fontSize: '0.7rem' }}>Reload</Button>
          </Tooltip>
        </Stack>
      </Stack>

      <Paper elevation={0} sx={{ ...cardStyle, p: 0, overflowX: 'auto' }}>
        <Box sx={{ minWidth: 1050 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: COLS, gap: 1.5, px: 2, py: 1.25, bgcolor: 'var(--sunk)', borderBottom: `1px solid ${colors.border}` }}>
            <Box sx={head}>Tablet</Box>
            <Box sx={head}>Last check-in</Box>
            <Box sx={head}>Battery</Box>
            <Box sx={head}>Blueprint</Box>
            <Box sx={head}>Temp</Box>
            <Box sx={head}>Wi-Fi</Box>
            <Box sx={head}>App</Box>
            <Box sx={{ ...head, textAlign: 'right' }}>Actions</Box>
          </Box>

          {data.tablets.length === 0 && (
            <Typography variant="body2" sx={{ color: colors.textMuted, py: 3, textAlign: 'center' }}>Esper reports no tablets.</Typography>
          )}

          {data.tablets.map((t, i) => {
            const on = isOnline(t);
            const ci: Level = on ? 'ok' : 'crit';
            // Offline: every value red. Online: plain, except what needs a hand on it.
            const valueColor = on ? colors.textSecondary : colors.error;
            // Below LOW_BATTERY the percentage is red, plugged in or not (matches the "below 20%" count).
            const lowBattery = (t.battery.level ?? 100) < LOW_BATTERY;
            const cmd = commandText(live[t.id] !== undefined ? live[t.id] : t.lastCommand);
            const appOld = Boolean(newestApp && t.app?.versionName && t.app.versionName !== newestApp);
            const isOpen = open === t.id;
            const last = i === data.tablets.length - 1;
            return (
              <Box key={t.id} sx={{ borderBottom: last ? 0 : `1px solid ${colors.border}` }}>
                <Box
                  onClick={() => setOpen(isOpen ? null : t.id)}
                  sx={{ display: 'grid', gridTemplateColumns: COLS, gap: 1.5, px: 2, py: 1.25, alignItems: 'center', cursor: 'pointer', '&:hover': { bgcolor: 'var(--sunk)' } }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <Dot level={ci} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: on ? colors.textPrimary : colors.error, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {label(t)}
                      </Typography>
                      <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.64rem', color: on ? colors.textMuted : colors.error }}>
                        {on ? t.name : `Offline · ${t.offlineReason || 'not connected'}`}
                      </Typography>
                      {t.environment && (
                        <Tooltip title={t.environment.reason}>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block', mt: 0.35, px: 0.7, py: 0.1, borderRadius: 1, fontFamily: FONT_DATA, fontSize: '0.6rem', fontWeight: 700,
                              letterSpacing: '0.06em', textTransform: 'uppercase', lineHeight: 1.5,
                              color: ours(t) ? colors.success : colors.warning,
                              border: `1px solid ${ours(t) ? colors.success : colors.warning}`,
                            }}
                          >
                            {envLabel(t)}{ours(t) ? '' : ' · view only'}
                          </Box>
                        </Tooltip>
                      )}
                      {notSignedIn(t) && (
                        <Tooltip title="The tablet is on, but Order Manager has not checked in with us for 2 minutes: signed out, closed, or off the orders screen. Orders will not reach this store.">
                          <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.64rem', fontWeight: 700, letterSpacing: '0.04em', color: colors.error }}>
                            NOT LOGGED INTO ORDER MANAGER
                          </Typography>
                        </Tooltip>
                      )}
                      {screenOff(t) && (
                        <Tooltip title="Someone turned the screen off (power button). Order Manager turns it back on after 5 minutes; if it stays off, tap it. A tablet with its screen off falls asleep after about 20 minutes and then misses orders.">
                          <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.64rem', fontWeight: 700, letterSpacing: '0.04em', color: colors.error }}>
                            SCREEN OFF since {new Date(t.screenOffSince as string).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Tooltip>
                      )}
                      {notSignedIn(t) && !t.store && (
                        <Typography sx={{ fontSize: '0.64rem', color: colors.textMuted }}>store not set up on this server</Typography>
                      )}
                      {cmd && (
                        <Typography sx={{ fontSize: '0.66rem', fontWeight: 600, color: cmd.level === 'none' ? colors.info : tone(cmd.level) }}>{cmd.text}</Typography>
                      )}
                    </Box>
                  </Stack>
                  <Box sx={{ ...cell, color: valueColor }}>{relative(newest(t.tabletSeenAt, t.lastCheckIn))}</Box>
                  <Box sx={{ ...cell, color: t.battery.holding ? colors.success : lowBattery ? colors.error : valueColor }}>
                    {t.battery.level === null ? '—' : `${t.battery.level}%`}
                    {t.battery.pluggedIn === null ? '' : t.battery.pluggedIn ? (
                      <Box component="span" sx={{ color: colors.success }}> · plugged in</Box>
                    ) : t.battery.holding ? (
                      <Tooltip title="On its charger: its charge limit has paused charging, so the tablet reports itself unplugged. It has held this level for 20+ minutes.">
                        <span> - holding</span>
                      </Tooltip>
                    ) : (
                      <Box component="span" sx={{ color: colors.error }}> · unplugged</Box>
                    )}
                    <Box sx={{ fontSize: '0.64rem', color: on ? colors.textMuted : colors.error }}>
                      {on ? 'reading' : 'last known'} {relative(t.snapshotAt)}
                    </Box>
                  </Box>
                  <Box sx={{ ...cell, color: valueColor, minWidth: 0 }}>
                    {!t.blueprint ? '—' : (
                      <Tooltip title={t.blueprint.name || `Esper did not give the blueprint's name (our key may not read blueprints). Id ${t.blueprint.id || 'unknown'}`}>
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {t.blueprint.name || `Unknown · ${(t.blueprint.id || '').slice(0, 8)}`}
                          </Box>
                          {blueprintVersion(t.blueprint) && (
                            <Box sx={{ fontSize: '0.64rem', color: t.blueprint.behind ? colors.warning : on ? colors.textMuted : colors.error }}>
                              {blueprintVersion(t.blueprint)}
                            </Box>
                          )}
                          {t.blueprint.moving && (
                            <Box sx={{ fontSize: '0.64rem', color: colors.info }}>
                              moving to {t.blueprint.assignedName || 'another blueprint'}
                            </Box>
                          )}
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ ...cell, color: valueColor }}>{t.battery.temperatureC === null ? '—' : `${t.battery.temperatureC}°C`}</Box>
                  <Box sx={{ ...cell, color: valueColor }}>{t.network.signal === null ? '—' : `${t.network.signal}/5`}</Box>
                  <Box sx={{ ...cell, color: !on || (!t.software && appOld) ? colors.error : colors.textSecondary }}>
                    {/* With the backend's verdict, the fleet-wide "newest version" check is not used: staging and
                        production tablets run different APKs on purpose. */}
                    <Tooltip title={t.software ? t.software.detail : appOld ? `Newest on the fleet is ${newestApp}` : ''}>
                      <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column' }}>
                        <span>{t.app?.versionName ? `${t.app.versionName}${t.app.versionCode ? ` (${t.app.versionCode})` : ''}` : '—'}</span>
                        {t.software && (
                          <Box component="span" sx={{ fontSize: '0.64rem', color: softwareColor(t.software.state) }}>
                            {t.software.label}
                          </Box>
                        )}
                      </Box>
                    </Tooltip>
                  </Box>
                  <Stack direction="row" spacing={0.75} justifyContent="flex-end" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={ours(t)
                      ? 'Asks this one tablet to check in with Esper now. If it is on, its row says Checked in within seconds. An unplugged tablet with its screen off is asleep, and only answers once it is plugged in or woken.'
                      : `View only on this dashboard. ${t.environment?.reason ?? ''}`}>
                      <span>
                        <Button size="small" variant="outlined" disabled={Boolean(busy[t.id]) || !ours(t)} onClick={() => run(t, 'refresh')} sx={{ fontSize: '0.7rem', minWidth: 0, px: 1.25 }}>
                          {busy[t.id] === 'refresh' ? <CircularProgress size={13} /> : 'Refresh'}
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title={ours(t) ? '' : `View only on this dashboard. ${t.environment?.reason ?? ''}`}>
                      <span>
                        <Button size="small" variant="outlined" color="error" disabled={Boolean(busy[t.id]) || !ours(t)} onClick={() => askReboot(t)} sx={{ fontSize: '0.7rem', minWidth: 0, px: 1.25 }}>
                          {busy[t.id] === 'reboot' ? <CircularProgress size={13} /> : 'Reboot'}
                        </Button>
                      </span>
                    </Tooltip>
                    <IconButton size="small" onClick={() => setOpen(isOpen ? null : t.id)} aria-label={isOpen ? 'Collapse' : 'Expand'} sx={{ color: colors.textMuted }}>
                      <Box component="span" sx={{ fontSize: '0.7rem', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>▾</Box>
                    </IconButton>
                  </Stack>
                </Box>

                <Collapse in={isOpen} unmountOnExit>
                  <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
                    <Typography sx={{ fontSize: '0.66rem', color: colors.textMuted, mb: 1.5 }}>
                      {on ? 'Online' : `Offline (${t.offlineReason || 'not connected'})`}
                      {t.appSignedIn === true ? ' · Order Manager signed in' : t.appSignedIn === false ? ' · Order Manager NOT signed in' : ''} · Readings as of {clock(t.snapshotAt)} · App check-in {clock(t.tabletSeenAt ?? null)} · Esper check-in {clock(t.lastCheckIn)}
                      {t.store ? ` · Store: ${t.store.name}` : ' · Not matched to a store'}
                    </Typography>
                    <Grid container spacing={2.5}>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Detail title="Network">
                          {t.network.ssid || '—'}{t.network.band ? ` · ${t.network.band}` : ''}<br />
                          IP {t.network.ipAddress || '—'}{t.network.linkSpeedMbps ? ` · ${t.network.linkSpeedMbps} Mbps link` : ''}
                        </Detail>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Detail title="Device">
                          {t.model || '—'}{t.os.version ? ` · Android ${t.os.version}` : ''}<br />
                          Security patch {t.os.securityPatch || '—'}<br />
                          Blueprint {t.blueprint ? (t.blueprint.name || `unknown (${t.blueprint.id || '—'})`) : '—'}
                          {t.blueprint && t.blueprint.version !== null ? ` · v${t.blueprint.version}` : ''}
                          {t.blueprint?.behind ? (
                            <Box component="span" sx={{ color: colors.warning }}>
                              {` · ${t.blueprint.latestVersion !== null ? `v${t.blueprint.latestVersion}` : 'a newer version'} is published`}
                            </Box>
                          ) : null}
                          {t.blueprint?.moving ? ` · moving to ${t.blueprint.assignedName || 'another blueprint'}` : ''}<br />
                          <Box component="span" sx={{ fontFamily: FONT_DATA, fontSize: '0.66rem' }}>{t.os.build || ''}</Box>
                        </Detail>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Detail title="Bluetooth (printer)">
                          {t.bluetooth.enabled === false ? 'Bluetooth is off' : t.bluetooth.paired.length === 0 ? 'Nothing paired' : t.bluetooth.paired.map((p) => (
                            <span key={p}>
                              {p} — <Box component="span" sx={{ color: t.bluetooth.connected.includes(p) ? colors.success : colors.textMuted }}>
                                {t.bluetooth.connected.includes(p) ? 'connected' : 'not connected'}
                              </Box><br />
                            </span>
                          ))}
                        </Detail>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Detail title="Sound & screen">
                          {STREAMS.map(([k, name]) => (t.settings.volumes[k] ?? null) !== null && (
                            <span key={k}>{name} volume {t.settings.volumes[k]}%<br /></span>
                          ))}
                          Brightness {t.settings.brightness ?? '—'}% · Screen timeout {screenTimeout(t.settings.screenOffTimeoutMs)}
                        </Detail>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Detail title="Battery & memory">
                          Health {t.battery.health || '—'}{t.cpuTemperatureC !== null ? ` · CPU ${Math.round(t.cpuTemperatureC)}°C` : ''}<br />
                          RAM free {t.memory.availableRamMb ?? '—'} of {t.memory.totalRamMb ?? '—'} MB<br />
                          Storage free {t.memory.availableStorageMb !== null ? Math.round(t.memory.availableStorageMb / 1024) : '—'} of {t.memory.totalStorageMb !== null ? Math.round(t.memory.totalStorageMb / 1024) : '—'} GB
                        </Detail>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Detail title="Location">
                          {t.location ? (
                            <a href={`https://www.google.com/maps?q=${t.location.lat},${t.location.lng}`} target="_blank" rel="noopener noreferrer" style={{ color: colors.info }}>
                              {t.location.lat.toFixed(5)}, {t.location.lng.toFixed(5)}
                            </a>
                          ) : '—'}
                        </Detail>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Detail title="Recent Esper activity">
                          {t.activity.length === 0 ? '—' : t.activity.map((a, k) => (
                            <span key={k}>
                              <Box component="span" sx={{ fontFamily: FONT_DATA, fontSize: '0.66rem', color: colors.textMuted }}>{clock(a.at)}</Box>
                              &nbsp;&nbsp;{a.message}<br />
                            </span>
                          ))}
                        </Detail>
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      </Paper>

      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700 }}>Reboot the {confirm ? label(confirm) : ''} tablet?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            It will be offline for about a minute and reopen on the order screen.
          </Typography>
          {waiting === undefined ? (
            <Typography variant="body2" sx={{ mt: 2, color: colors.textMuted }}>Checking for waiting orders…</Typography>
          ) : waiting ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {waiting} order{waiting === 1 ? ' is' : 's are'} waiting at this store right now.
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => { const t = confirm; setConfirm(null); if (t) run(t, 'reboot'); }}
          >
            Reboot
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TabletHealth;
